import { http } from './request';
import { LEAD_URLS as API_URLS } from './urls';
import type { ImSessionLeadStatus } from '../../common/enums/imSessionStatus';
import type { ReplyContent } from './imSessions';
import type { CategoryCollectTask } from './categories';

export type LeadCategory = string;
export type LeadPlatform = 'douyin' | 'kuaishou' | 'tiktok' | 'xiaohongshu' | 'instagram';
export type LeadSceneType = 'comment' | 'danmaku';
export type LeadIntentionLevel = 'HIGH' | 'LOW' | 'NONE';
export type LeadFollowStatus = 'YES' | 'NO' | 'IN_PROGRESS' | 'FOLLOWED' | 'UNFOLLOWED' | 'FAILED';

export interface FetchLeadsParams {
  shopId?: string;
  categoryId?: LeadCategory;
  /** 关键词搜索，同时匹配 actorName、contentText、sourceTitle */
  keyword?: string;
  platform?: LeadPlatform;
  sceneType?: LeadSceneType;
  intentionLevel?: LeadIntentionLevel;
  followStatus?: LeadFollowStatus;
  page?: number;
  size?: number;
}

export interface LeadItem {
  id: number;
  itemId?: string;
  leadKey?: string;
  /** @deprecated 旧接口遗留，请使用 leadKey */
  lead_key?: string;
  platform: LeadPlatform | string;
  sceneType: LeadSceneType;
  category: LeadCategory | string;
  categoryId?: string;
  categoryIds?: string[];
  categoryLabel?: string;
  categoryFullLabel?: string;
  actorName: string;
  /** @deprecated 旧接口遗留，请使用 actorName */
  actor_name?: string;
  /** 线索用户 stable_user_id，抖音下对应 sec_uid */
  actorStableUserId?: string;
  /** @deprecated 旧字段名，请使用 actorStableUserId */
  actorSecUid?: string;
  actorPublicUserId?: string;
  actorAvatar?: string;
  actorProfileUrl?: string;
  contentText: string;
  intentionLevel: LeadIntentionLevel;
  followStatus: LeadFollowStatus;
  sourceTitle: string;
  /** 来源内容平台 ID，视频场景下对应 aweme_id */
  sourceContentPlatformId?: string;
  /** @deprecated 旧字段名，请使用 sourceContentPlatformId */
  sourceId?: string;
  /** 来源发布者 stable_user_id */
  sourceActorStableUserId?: string;
  /** 来源发布者昵称 */
  sourceActorName?: string;
  /** 来源发布者头像 */
  sourceActorAvatar?: string;
  /** 来源头像，后端新字段 */
  sourceAvatar?: string;
  /** 来源发布者主页链接 */
  sourceActorProfileUrl?: string;
  leadUrl?: string;
  eventTime: number;
  crawlTime: number;
  replyContent?: ReplyContent | null;
  replyDisplayText?: string;
  replySendText?: string;
  followedEcomAccountId?: string;
  followedEcomAccountName?: string;
}

export interface LeadListResponse {
  total: number;
  list: LeadItem[];
}

export interface FollowLeadPayload {
  id: number;
  ecomAccountId: string;
  message: string;
}

export interface FollowLeadResponse {
  updated: number;
  messageSent: boolean;
  sendCaptureCount?: number;
  screenshotPath?: string;
}

export interface UpdateLeadFollowStatusPayload {
  id: number;
  followStatus: ImSessionLeadStatus;
  ecomAccountId?: string;
  failureReason?: string;
  replyContent?: ReplyContent | null;
}

export interface UpdateLeadFollowStatusResponse {
  id: number;
  updated: number;
  followStatus: ImSessionLeadStatus;
  failureReason?: string;
}

export interface LeadsOverviewResponse {
  windowDays: number;
  totalLeads: number;
  highIntentLeads: number;
  followedLeads: number;
}

export interface SubmitPublicLeadCollectPayload {
  shopId?: string;
  ecomAccountId: string;
  categoryId?: string;
  category?: string;
  collectType?: 'all' | 'comment' | 'danmaku';
  commentEnabled?: boolean;
  danmakuEnabled?: boolean;
  videoSearchCount?: number;
  commentPerVideoLimit?: number;
  liveRoomSearchCount?: number;
  danmakuCollectDurationSec?: number;
  danmakuMaxMessagesPerRoom?: number;
}

export interface SubmitPublicLeadCollectResponse {
  message: string;
  running?: boolean;
  shopId?: string;
  ecomAccountId?: string;
  categoryId?: string;
  category?: string;
  billableLeadCount?: number;
  persistedCount?: number;
  count?: number;
  computeCharge?: unknown;
  tasks?: CategoryCollectTask[];
}

/**
 * 公域线索列表
 */
export const fetchLeads = async (params: FetchLeadsParams = {}) => {
  const query: FetchLeadsParams = {
    page: params.page ?? 1,
    size: params.size ?? 10,
  };

  if (params.shopId) {
    query.shopId = params.shopId;
  }
  if (params.categoryId) {
    query.categoryId = params.categoryId;
  }
  if (params.keyword) {
    query.keyword = params.keyword;
  }
  if (params.platform) {
    query.platform = params.platform;
  }
  if (params.sceneType) {
    query.sceneType = params.sceneType;
  }
  if (params.intentionLevel) {
    query.intentionLevel = params.intentionLevel;
  }
  if (params.followStatus) {
    query.followStatus = params.followStatus;
  }

  return http.get<LeadListResponse>(API_URLS.leads, { params: query });
};

/**
 * 公域线索总揽
 */
export const fetchLeadsOverview = (params: { shopId?: string; categoryId?: LeadCategory } = {}) => {
  return http.get<LeadsOverviewResponse>(API_URLS.leadsOverview, {
    params: {
      ...(params.shopId ? { shopId: params.shopId } : {}),
      ...(params.categoryId ? { categoryId: params.categoryId } : {}),
    },
  });
};

/**
 * 更新公域线索跟进状态
 */
export const followLead = (payload: FollowLeadPayload) => {
  return http.post<FollowLeadResponse>(API_URLS.leadsFollow, payload);
};

/**
 * 手动回写公域线索私信发送状态
 */
export const updateLeadFollowStatus = (payload: UpdateLeadFollowStatusPayload) => {
  return http.post<UpdateLeadFollowStatusResponse>(API_URLS.leadsStatus, payload);
};

export const submitPublicLeadCollect = (payload: SubmitPublicLeadCollectPayload) => {
  return http.post<SubmitPublicLeadCollectResponse>(API_URLS.leadsCollect, payload);
};

export interface GenerateLeadSuggestionsPayload {
  shopId?: string;
  lead: {
    leadKey: string;
    platform?: string;
    sceneType?: string;
    category?: string;
    actorName?: string;
    contentText?: string;
    intentionLevel?: string;
    followStatus?: string;
    sourceTitle?: string;
    productId?: string;
    platformProductId?: string;
    productName?: string;
    productUrl?: string;
  };
}

export interface GenerateLeadSuggestionsResponse {
  result: {
    replyText: string;
    hasAssociatedProduct?: boolean;
    associatedProduct?: {
      productId?: string;
      platformProductId?: string;
      productName?: string;
      productUrl?: string;
    } | null;
  };
}

export const generateLeadSuggestions = (payload: GenerateLeadSuggestionsPayload) => {
  return http.post<GenerateLeadSuggestionsResponse>(API_URLS.leadsSuggestions, payload, { timeout: 300000 });
};
